import type { ModelSchema } from '@arcanum-hq/shared-types';

/**
 * Deep-link parser + serializer (agent-ux-optimization Phase 2 Session 5).
 *
 * Recognizes the agent's `[arcanum:goto:<surface>?<params>][#fragment]`
 * markdown extension. The system prompt section `<deep_link_grammar>` (Phase 1)
 * instructs the agent on emit syntax; this module is the consumer.
 *
 * URL encoding is end-to-end (Q13) — `serializeDeepLink` percent-encodes every
 * value; `parseDeepLink` decodes on read. Round-trip fidelity verified by
 * `__tests__/deep-links.spec.ts`.
 */

export type DeepLinkSurface = 'workbench' | 'signal' | 'model' | 'variables';

export interface DeepLinkParts {
  surface: DeepLinkSurface;
  /** Query params, decoded. e.g. `{ cell: 'DealSummary!F23' }`. */
  params: Record<string, string>;
  /** Post-`#` fragment, decoded. e.g. `assumption/ExitCapRate`. */
  hash: string | null;
  /** Original `[arcanum:goto:...]` string for debugging / error reporting. */
  raw: string;
}

const DEEP_LINK_REGEX =
  /^\[arcanum:goto:([^=?\]#]+)(?:=([^\]]+))?(?:\?([^\]#]+))?(?:#([^\]]+))?\]$/;

const KNOWN_SURFACES: ReadonlySet<DeepLinkSurface> = new Set([
  'workbench', 'signal', 'model', 'variables',
]);

function isKnownSurface(s: string): s is DeepLinkSurface {
  return (KNOWN_SURFACES as ReadonlySet<string>).has(s);
}

/**
 * Parse a single `[arcanum:goto:...]` string into structured parts. Returns
 * `null` if the syntax doesn't match (caller can render literal text).
 *
 * Use `splitTextWithExtensions` from `markdown-extensions.ts` for prose-walk
 * usage; this helper handles single-token shapes (e.g. test fixtures, the
 * round-trip serializer).
 */
export function parseDeepLink(raw: string): DeepLinkParts | null {
  const match = raw.match(DEEP_LINK_REGEX);
  if (!match) return null;
  const [, surface, , query, hash] = match;
  if (!isKnownSurface(surface)) return null;

  const params: Record<string, string> = {};
  if (query) {
    for (const pair of query.split('&')) {
      const [k, v] = pair.split('=');
      if (!k) continue;
      params[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
    }
  }
  return {
    surface,
    params,
    hash: hash ? decodeURIComponent(hash) : null,
    raw,
  };
}

/**
 * Serialize back to `[arcanum:goto:...]` form. Symmetric inverse of
 * `parseDeepLink` — every parameter value is percent-encoded.
 */
export function serializeDeepLink(parts: DeepLinkParts): string {
  let body = `[arcanum:goto:${parts.surface}`;
  const entries = Object.entries(parts.params);
  if (entries.length > 0) {
    const query = entries
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    body += `?${query}`;
  }
  if (parts.hash) {
    body += `#${encodeURIComponent(parts.hash)}`;
  }
  return `${body}]`;
}

/**
 * Resolution result — used by `<DeepLink>` for I6 pre-validation. Invalid
 * targets render `<Chip variant="invalid">` with no-op click; valid ones
 * become interactive `<Chip variant="agent">` buttons that navigate.
 */
export type ResolveResult =
  | {
      ok: true;
      target: { surface: DeepLinkSurface; route: string; search: Record<string, unknown> };
    }
  | {
      ok: false;
      reason: 'unknown-named-range' | 'unknown-cell-address' | 'no-schema' | 'unsupported-surface';
    };

/**
 * Validate that the deep-link target actually exists in the active model.
 * Returns the TanStack Router `navigate({ to, search })` shape when ok.
 *
 * Schema validation is best-effort against the type-only `ModelSchema`
 * subset surfaced through `useModel` — when the schema fields aren't
 * structured (e.g. `inputs` / `outputs` arrays carry raw shapes), we accept
 * any string match against the namedRange / cell. Unknown surfaces fail
 * deterministically.
 */
export function resolveDeepLinkTarget(
  parts: DeepLinkParts,
  modelSchema: ModelSchema | null,
): ResolveResult {
  if (!isKnownSurface(parts.surface)) {
    return { ok: false, reason: 'unsupported-surface' };
  }
  // Cell / namedRange validation is gated on a loaded schema. `null` →
  // defensive invalid (the agent emitted before schema mounted).
  if (parts.params.cell || parts.params.namedRange) {
    if (!modelSchema) return { ok: false, reason: 'no-schema' };
    const namedRange = parts.params.namedRange;
    if (namedRange) {
      const allRanges = (modelSchema as { allNamedRanges?: Array<unknown> }).allNamedRanges ?? [];
      const found = allRanges.some((nr) => {
        if (typeof nr === 'string') return nr === namedRange;
        if (nr && typeof nr === 'object' && 'name' in nr) {
          return (nr as { name: unknown }).name === namedRange;
        }
        return false;
      });
      if (!found) return { ok: false, reason: 'unknown-named-range' };
    }
    // `cell` validation deferred — the cell-address space (`Sheet!A1`) lives
    // in `inputs` + `outputs` + computed-cell tables that the type-only
    // ModelSchema subset doesn't structure. /implement-time policy: accept
    // the string verbatim; the Model-tab consumer can fail-soft on an
    // unknown cell at navigation time.
  }

  const search: Record<string, unknown> = {};
  // agent-ux-optimization Phase 6 Session 3 (2026-05-08) — Builder tabs
  // renamed `?tab=conversation|model` → `?tab=workbench|signal|model`.
  // Surface enum on the deep-link side already used `workbench` for the
  // chat surface, so the only real change here is the destination tab
  // value (`'conversation'` → `'workbench'`) + the new `'signal'` arm.
  if (parts.surface === 'model') search.tab = 'model';
  if (parts.surface === 'signal') search.tab = 'signal';
  if (parts.surface === 'workbench') search.tab = 'workbench';
  if (parts.params.cell) search.cell = parts.params.cell;
  if (parts.params.namedRange) search.namedRange = parts.params.namedRange;

  return {
    ok: true,
    target: {
      surface: parts.surface,
      route: '/builder',
      search,
    },
  };
}
